/**
 * A field list for a detail page.
 *
 * A real `<dl>` rather than a grid of divs: the term/description relationship
 * is what tells a screen reader which value belongs to which label, and a
 * two-column grid conveys that visually only.
 *
 * Lives in `components/domain` because two features' detail pages need it, and
 * plan §3.1 forbids one feature importing another — the startup and pilot
 * detail pages are siblings, not a hierarchy.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.5, 7.2
 */
import { Fragment } from 'react';

import { cn } from '../../lib/cn';

export interface DefinitionListItem {
  label: string;
  value: React.ReactNode;
}

export interface DefinitionListProps {
  items: readonly DefinitionListItem[];
  className?: string;
}

export function DefinitionList({
  items,
  className,
}: DefinitionListProps): React.JSX.Element {
  return (
    /* A 170px label column beside the values, per the prototype, collapsing to
       one column below 920px — a fixed label column at 375px leaves the values
       about ten characters wide. `grid` on the `<dl>` itself, with the `<dt>`
       and `<dd>` as direct children, so the term/description pairing survives:
       wrapping each pair in a `<div>` is what usually breaks it. */
    <dl className={cn('grid grid-cols-1 min-[920px]:grid-cols-[170px_1fr]', className)}>
      {items.map((item) => (
        <Fragment key={item.label}>
          <dt className="border-gray-100 text-body border-b py-2.5 text-xs min-[920px]:border-b-0 min-[920px]:pb-0">
            {item.label}
          </dt>
          <dd className="border-gray-100 text-body border-b py-2.5 text-[13px] last:border-b-0">
            {item.value}
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
